import { hasYesOrNo } from 'models/validate';
import { DEFAULT_LATEST, THIS_MONTH, TODAY } from 'constants/dateLimits';
import { ValidateJSModel } from 'forms/shared/types';
import { substanceDrugTypeValidator } from 'forms/sections/substance/shared/drugTypeValidator';
import { getSafeDateTime, isSafeDateLater, setToStartOfMonth } from 'helpers/date';
import { formConfig } from 'config/forms';
import { SafeDateTime } from 'helpers/types';
import { isPVQForm } from 'helpers/form';
import { SubstanceDrugsUsageItemModel } from '../types';

const substanceDrugsUsageItemValidator: ValidateJSModel<SubstanceDrugsUsageItemModel> = {
  DrugType: (value, attributes, attributeName, options = {}) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      return { presence: true, hasValue: true };
    }
    return substanceDrugTypeValidator;
  },
  DrugTypeOther: (value, attributes, attributeName, options) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      if (attributes?.DrugType?.value === 'Other') {
        return { presence: true, hasValue: true };
      }
    }
    return {};
  },
  FirstUse: (value, attributes, attributeName, options) => ({
    presence: true,
    date:
      {
        requireDay: false,
        earliest: setToStartOfMonth(options?.applicantBirthdate),
        tooEarly: 'error.alcohol.negativeImpact.occurred.error.tooEarly',
        latest: DEFAULT_LATEST
      },
  }),
  RecentUse: (value, attributes, attributeName, options) => {
    if (!attributes?.FirstUse) {
      return {};
    }
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      if (!options?.applicantBirthdate || !attributes?.FirstUse) { return {}; }
      const applicantBirthdate: SafeDateTime = getSafeDateTime(options?.applicantBirthdate);
      const firstUseDate: SafeDateTime = getSafeDateTime(attributes?.FirstUse);
      const sixteenthBirthdate: SafeDateTime = getSafeDateTime(applicantBirthdate?.datetime?.plus({ years: 16 }));
      const twentyFirstBirthday: SafeDateTime = getSafeDateTime(applicantBirthdate?.datetime?.plus({ years: 21 }));
      const fiveYearsAgo: SafeDateTime = getSafeDateTime(DEFAULT_LATEST.minus({ years: 5 }));
      // if 21 bday is before (or on) today applicant is over 21
      const isOverTwentyOne: boolean = isSafeDateLater(getSafeDateTime(DEFAULT_LATEST), twentyFirstBirthday);
      const stauteOfLimitation = isOverTwentyOne ? fiveYearsAgo : sixteenthBirthdate;
      const isFirstUseBeforeStaute = isSafeDateLater(firstUseDate, stauteOfLimitation);

      return {
        presence: true,
        date: {
          requireDay: false,
          requireYear: false,
          latest: DEFAULT_LATEST,
          earliest: isFirstUseBeforeStaute ? setToStartOfMonth(firstUseDate).datetime : setToStartOfMonth(stauteOfLimitation).datetime,
          tooEarly: isFirstUseBeforeStaute ? 'error.drugUsage.min' : 'error.beforeDuration'
        },
      };
    }

    if (!attributes?.FirstUse) { return {}; }

    const formTypeConfig = options?.formType && formConfig[options?.formType as keyof typeof formConfig];
    const applicantEighteenthBirthdate: SafeDateTime | undefined = options?.applicantBirthdate && getSafeDateTime(getSafeDateTime(options?.applicantBirthdate)?.datetime?.plus({ years: 18 }));
    const years: any = formTypeConfig && formTypeConfig.SUBSTANCE_DRUG_USE_YEARS;

    let beyondRequired: SafeDateTime = getSafeDateTime(THIS_MONTH);
    let useDuration = false;

    if (years && beyondRequired?.valid && typeof years === 'number') {
      if (options?.formType && options?.formType === 'SF86' && applicantEighteenthBirthdate?.valid
      && applicantEighteenthBirthdate.datetime.year < (TODAY.year - years)) {
        beyondRequired = setToStartOfMonth(applicantEighteenthBirthdate);
      } else {
        beyondRequired.datetime = beyondRequired?.datetime.minus({ years });
      }

      useDuration = isSafeDateLater(beyondRequired, getSafeDateTime(attributes?.FirstUse));
    }

    return {
      presence: true,
      date: {
        requireDay: false,
        requireYear: false,
        latest: DEFAULT_LATEST,
        earliest: useDuration ? setToStartOfMonth(beyondRequired).datetime : attributes?.FirstUse,
        tooEarly: useDuration ? 'error.beforeDuration' : 'error.drugUsage.min',
      },
    };
  },
  HowManyTimes: (value, attributes, attributeName, options) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      return { presence: true, hasValue: true };
    }
    return {};
  },
  Reason: (value, attributes, attributeName, options) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      return { presence: true, hasValue: true };
    }
    return {};
  },
  UseWhileEmployedPosition: (value, attributes, attributeName, options) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      if (attributes?.UseWhileEmployed?.value === 'Yes') { return { presence: true, hasValue: true }; }
    }
    return {};
  },
  NatureOfUse: (value, attributes, attributeName, options) => {
    const isPVQ = isPVQForm(options?.formType);
    if (!isPVQ) {
      return { presence: true, hasValue: true };
    }
    return {};
  },
  Explanation: (value, attributes, attributeName, options) => {
    if (options?.requireUseInFuture === false) { return {}; }
    return { presence: true, hasValue: true };
  },
  UseWhileEmployed: (value, attributes, attributeName, options) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      return { presence: true, hasValue: { validator: hasYesOrNo } };
    }
    if (options?.requireUseWhileEmployed === false) { return {}; }
    return { presence: true, hasValue: { validator: hasYesOrNo } };
  },
  UseWhileEmployedExplanation: (value, attributes, attributeName, options) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      if (attributes?.UseWhileEmployed?.value === 'Yes') { return { presence: true, hasValue: true }; }
    }
    return {};
  },
  UseWithClearance: (value, attributes, attributeName, options) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      return { presence: true, hasValue: { validator: hasYesOrNo } };
    }
    if (options?.requireUseWithClearance === false) { return {}; }
    return { presence: true, hasValue: { validator: hasYesOrNo } };
  },
  UseWithClearanceExplanation: (value, attributes, attributeName, options) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      if (attributes?.UseWithClearance?.value === 'Yes') { return { presence: true, hasValue: true }; }
    }
    return {};
  },
  UseInFuture: (value, attributes, attributeName, options) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      return { presence: true, hasValue: { validator: hasYesOrNo } };
    }
    if (options?.requireUseInFuture === false) { return {}; }
    return { presence: true, hasValue: { validator: hasYesOrNo } };
  },
  UseInFutureExplanation: (value, attributes, attributeName, options) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      return { presence: true, hasValue: true };
    }
    return {};
  },
  Comments: {},
};

export default substanceDrugsUsageItemValidator;
